import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton,
    Switch, FormControlLabel, Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { ExpensePolicyService } from '../../services/expense-policy.service';
import { AccountService } from '../../services/account.service';
import { ExpensePolicyDTO, ExpensePolicyTierDTO } from '../../interfaces/expense.interface';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { IRoleSummary } from '../../interfaces/role.interface';
import SearchableSelect from '../common/SearchableSelect';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { leafAccounts, storedAccountProblems } from '../../utils/postingAccounts';

const ExpensePolicyManagement: React.FC = () => {
    // Writes are EXPENSE_POLICY_MANAGE server-side; EXPENSE_POLICY_VIEW (Accountant) is read-only.
    const canManage = usePermission(PERMISSIONS.EXPENSE_POLICY_MANAGE);
    const [policy, setPolicy] = useState<ExpensePolicyDTO | null>(null);
    const [loading, setLoading] = useState(true);
    const [liabilityAccounts, setLiabilityAccounts] = useState<Account[]>([]);
    const [assetAccounts, setAssetAccounts] = useState<Account[]>([]);

    const [threshold, setThreshold] = useState('0');
    const [payableAccountId, setPayableAccountId] = useState('');
    const [advanceAccountId, setAdvanceAccountId] = useState('');
    const [allowStandalone, setAllowStandalone] = useState(true);
    const [allowPayroll, setAllowPayroll] = useState(true);
    // W3-U
    const [allowPerDiemOverride, setAllowPerDiemOverride] = useState(false);
    const [cardReceivableAccountId, setCardReceivableAccountId] = useState('');
    // stored accounts the server would now refuse (group / inactive / wrong type): flagged on load and cleared so a valid one is picked
    const [invalidStored, setInvalidStored] = useState<string[]>([]);

    const [tierDialogOpen, setTierDialogOpen] = useState(false);
    const [editingTier, setEditingTier] = useState<ExpensePolicyTierDTO | null>(null);
    const [tierMin, setTierMin] = useState('0');
    const [tierMax, setTierMax] = useState('');
    const [tierRole, setTierRole] = useState('');
    const [roles, setRoles] = useState<IRoleSummary[]>([]);
    const [tierSeq, setTierSeq] = useState('1');

    const toast = useToast();

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [pol, liab, asset, roleList] = await Promise.all([
                ExpensePolicyService.getByInstitution(),
                AccountService.getAccountsByType('LIABILITY'),
                AccountService.getAccountsByType('ASSET'),
                ExpensePolicyService.listApproverRoles().catch(() => [] as IRoleSummary[]),
            ]);
            setRoles(roleList);
            const liabLeaves = leafAccounts(liab);
            const assetLeaves = leafAccounts(asset);
            setLiabilityAccounts(liabLeaves);
            setAssetAccounts(assetLeaves);
            if (pol) {
                setPolicy(pol);
                setThreshold(String(pol.defaultReceiptThreshold));
                const problems = storedAccountProblems([
                    { label: 'Employee payable account', id: pol.employeePayableAccountId, options: liabLeaves, all: liab },
                    { label: 'Advance account', id: pol.advanceAccountId, options: assetLeaves, all: asset },
                    { label: 'Card receivable account', id: pol.cardReceivableAccountId, options: assetLeaves, all: asset },
                ]);
                setInvalidStored(problems.map(p => p.message));
                const bad = new Set(problems.map(p => p.label));
                setPayableAccountId(bad.has('Employee payable account') ? '' : pol.employeePayableAccountId);
                setAdvanceAccountId(bad.has('Advance account') ? '' : pol.advanceAccountId);
                setAllowStandalone(pol.allowStandaloneReimbursement);
                setAllowPayroll(pol.allowPayrollAttach);
                setAllowPerDiemOverride(Boolean(pol.allowPerDiemOverride)); // W3-U
                setCardReceivableAccountId(bad.has('Card receivable account') ? '' : (pol.cardReceivableAccountId ?? '')); // W3-U
            }
        } catch (e: any) {
            toast.showError(e?.response?.data ?? 'Failed to load policy');
        } finally {
            setLoading(false);
        }
    };

    const savePolicy = async () => {
        if (!payableAccountId || !advanceAccountId) {
            toast.showError('Payable and advance accounts are required');
            return;
        }
        try {
            const saved = await ExpensePolicyService.upsert({
                defaultReceiptThreshold: Number(threshold) || 0,
                employeePayableAccountId: payableAccountId,
                advanceAccountId,
                allowStandaloneReimbursement: allowStandalone,
                allowPayrollAttach: allowPayroll,
                allowPerDiemOverride, // W3-U
                ...(cardReceivableAccountId ? { cardReceivableAccountId }
                    : policy?.cardReceivableAccountId ? { clearCardReceivableAccount: true } : {}), // W3-U
            });
            setPolicy(saved);
            toast.showSuccess('Policy saved');
        } catch (e: any) {
            toast.showError(e?.response?.data ?? 'Save failed');
        }
    };

    const openNewTier = () => {
        setEditingTier(null);
        setTierMin('0');
        setTierMax('');
        setTierRole(roles.find(r => r.isAdmin)?.id ?? roles[0]?.id ?? '');
        setTierSeq(String((policy?.tiers.length ?? 0) + 1));
        setTierDialogOpen(true);
    };

    const openEditTier = (t: ExpensePolicyTierDTO) => {
        setEditingTier(t);
        setTierMin(String(t.minAmount));
        setTierMax(t.maxAmount != null ? String(t.maxAmount) : '');
        setTierRole(t.approverRoleId ?? '');
        setTierSeq(String(t.sequence));
        setTierDialogOpen(true);
    };

    const saveTier = async () => {
        if (!policy) {
            toast.showError('Save policy first');
            return;
        }
        if (!tierRole) {
            toast.showError('Pick the approver role for this tier');
            return;
        }
        const req = {
            minAmount: Number(tierMin) || 0,
            maxAmount: tierMax ? Number(tierMax) : null,
            approverRoleId: tierRole,
            sequence: Number(tierSeq) || 1,
        };
        try {
            if (editingTier) await ExpensePolicyService.updateTier(editingTier.id, req);
            else await ExpensePolicyService.addTier(req);
            setTierDialogOpen(false);
            await loadAll();
            toast.showSuccess('Tier saved');
        } catch (e: any) {
            toast.showError(e?.response?.data ?? 'Save failed');
        }
    };

    const deleteTier = async (id: string) => {
        if (!window.confirm('Delete this tier?')) return;
        try {
            await ExpensePolicyService.deleteTier(id);
            await loadAll();
            toast.showSuccess('Tier deleted');
        } catch (e: any) {
            toast.showError(e?.response?.data ?? 'Delete failed');
        }
    };

    if (loading) return <Box sx={{ p: 3 }}>Loading…</Box>;

    return (
        <Box sx={{ p: 3 }} data-help-page="expense.policy.page">
            <Typography variant="h5" sx={{ mb: 2 }}>Expense Policy</Typography>

            {invalidStored.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }} data-testid="policy-invalid-accounts">
                    {invalidStored.map(m => <div key={m}>{m}</div>)}
                    Pick a valid posting account for each and save the policy: it cannot be saved while one is missing.
                </Alert>
            )}
            <Paper sx={{ p: 2, mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }} data-help-section="expense.policy.settings">
                <TextField data-help="expense.policy.threshold" label="Default receipt threshold" type="number" disabled={!canManage}
                           value={threshold} onChange={e => setThreshold(e.target.value)} />
                <AccountAutocomplete data-help="expense.policy.payableAccount"
                    accounts={liabilityAccounts}
                    value={payableAccountId}
                    onChange={setPayableAccountId}
                    label="Employee Payable Account (Liability)"
                />
                <AccountAutocomplete data-help="expense.policy.advanceAccount"
                    accounts={assetAccounts}
                    value={advanceAccountId}
                    onChange={setAdvanceAccountId}
                    label="Advance Account (Asset)"
                />
                <FormControlLabel data-help="expense.policy.allowStandalone" control={<Switch disabled={!canManage} checked={allowStandalone}
                                                    onChange={e => setAllowStandalone(e.target.checked)} />}
                                  label="Allow standalone reimbursement (out-of-cycle payout)" />
                <FormControlLabel data-help="expense.policy.allowPayroll" control={<Switch disabled={!canManage} checked={allowPayroll}
                                                    onChange={e => setAllowPayroll(e.target.checked)} />}
                                  label="Allow payroll-attach reimbursement" />
                {/* ---- W3-U ---- */}
                <FormControlLabel data-help="expense.policy.allowPerDiem" control={<Switch disabled={!canManage} checked={allowPerDiemOverride}
                                                    onChange={e => setAllowPerDiemOverride(e.target.checked)} />}
                                  label="Allow editing a per-diem amount on a claim line (otherwise locked to the rate table)" />
                <AccountAutocomplete data-help="expense.policy.cardReceivable"
                    accounts={assetAccounts}
                    value={cardReceivableAccountId}
                    onChange={setCardReceivableAccountId}
                    label="Card receivable account (unclaimed personal card spend)"
                />
                {/* ---- end W3-U ---- */}
                {canManage && (
                    <Box>
                        <Button variant="contained" data-help="expense.policy.save" onClick={savePolicy}>Save Policy</Button>
                    </Box>
                )}
            </Paper>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6">Approval Tiers</Typography>
                {canManage && <Button data-help="expense.policy.addTier" variant="contained" onClick={openNewTier} disabled={!policy}>Add Tier</Button>}
            </Box>

            <TableContainer component={Paper} data-help-section="expense.policy.tiers">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="expense.policy.col.sequence">Sequence</TableCell>
                            <TableCell align="right" data-help="expense.policy.col.min">Min Amount</TableCell>
                            <TableCell align="right" data-help="expense.policy.col.max">Max Amount</TableCell>
                            <TableCell data-help="expense.policy.col.role">Approver Role</TableCell>
                            <TableCell align="right" data-help="expense.policy.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!policy || policy.tiers.length === 0 ? (
                            <TableRow><TableCell colSpan={5}>No tiers configured yet.</TableCell></TableRow>
                        ) : policy.tiers.map(t => (
                            <TableRow key={t.id}>
                                <TableCell data-help="expense.policy.col.sequence">{t.sequence}</TableCell>
                                <TableCell align="right" data-help="expense.policy.col.min">{t.minAmount}</TableCell>
                                <TableCell align="right" data-help="expense.policy.col.max">{t.maxAmount ?? '∞'}</TableCell>
                                <TableCell data-help="expense.policy.col.role">{t.approverRoleName ?? 'Institution admin only'}</TableCell>
                                <TableCell align="right">
                                    {canManage && (
                                        <>
                                            <IconButton size="small" data-help="expense.policy.action.edit" onClick={() => openEditTier(t)}><EditIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" data-help="expense.policy.action.delete" onClick={() => deleteTier(t.id)}><DeleteIcon fontSize="small" /></IconButton>
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={tierDialogOpen} onClose={() => setTierDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editingTier ? 'Edit Tier' : 'New Tier'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField data-help="expense.policy.tier.sequence" label="Sequence" type="number" value={tierSeq} onChange={e => setTierSeq(e.target.value)} />
                    <TextField data-help="expense.policy.tier.min" label="Min Amount" type="number" value={tierMin} onChange={e => setTierMin(e.target.value)} />
                    <TextField data-help="expense.policy.tier.max" label="Max Amount (blank for unlimited)" type="number"
                               value={tierMax} onChange={e => setTierMax(e.target.value)} />
                    <SearchableSelect<IRoleSummary> data-help="expense.policy.tier.role"
                        options={roles}
                        value={tierRole}
                        onChange={id => setTierRole(id)}
                        getOptionLabel={r => r.name}
                        label="Approver Role"
                        placeholder="Select a role"
                        clearable={false}
                        helperText="Holders of this role (and institution admins) can approve at this tier."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTierDialogOpen(false)}>Cancel</Button>
                    <Button onClick={saveTier} variant="contained" data-help="expense.policy.tier.save">Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ExpensePolicyManagement;
